/**
 * Portal v2 action centre (`/portal/action-center`).
 *
 * The one page that answers "what should I do next". Domains are ordered
 * worst-first and each carries a single top issue — the ordering is the
 * product, not presentation, so a customer with 28 domains knows where to
 * start.
 *
 * Scores come from the same scorer the MSP's own compliance scorecard uses, so
 * a customer is never told a different priority than their provider sees.
 *
 * Healthy domains are shown after the ones needing attention rather than hidden:
 * "nothing to do here" is information a customer wants confirmed.
 */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ChevronRight, ListChecks } from "lucide-react";
import { Skeleton } from "@ddmarc/ui";
import { portalApi, type PortalActionCenterItem } from "@/lib/portal-api";
import { usePortalScope } from "@/lib/portal-scope-context";
import { CardV2, KpiTile, SectionHeader } from "@/components/primitives";

const GRADE_COLOR: Record<string, string> = {
  A: "var(--v2-good)",
  B: "var(--v2-good)",
  C: "var(--v2-warn)",
  D: "var(--v2-danger)",
  F: "var(--v2-danger)",
};

function GradeBadge({ grade, score }: { grade: string; score: number }) {
  const color = GRADE_COLOR[grade] ?? "var(--v2-fg-faint)";
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <span
        className="inline-flex h-6 w-6 items-center justify-center rounded font-mono-data text-[11px] font-bold text-white"
        style={{ background: color }}
      >
        {grade}
      </span>
      <span className="tnum font-mono-data text-[12px]" style={{ color }}>
        {score}/100
      </span>
    </span>
  );
}

function ActionRow({ item }: { item: PortalActionCenterItem }) {
  return (
    <Link
      to="/portal/domains/$domainId"
      params={{ domainId: String(item.domain_id) }}
      className="grid grid-cols-[1.4fr_1.6fr_auto_auto] items-center gap-3 px-4 py-3 text-[12.5px] transition-colors hover:bg-secondary/60"
    >
      <span className="truncate font-mono-data text-foreground">{item.domain}</span>
      <span className="truncate text-muted-foreground">
        {item.top_issue ?? "No action needed"}
      </span>
      <GradeBadge grade={item.grade} score={item.score} />
      <ChevronRight className="h-4 w-4 text-[var(--v2-fg-faint)]" aria-hidden="true" />
    </Link>
  );
}

export function PortalActionCenterV2Page() {
  const { selectedDomainId } = usePortalScope();

  const { data, isLoading } = useQuery({
    queryKey: ["portal", "action-center", selectedDomainId],
    queryFn: () => portalApi.getActionCenter({ domain_id: selectedDomainId }),
  });

  const items = data?.items ?? [];
  const needsAttention = items.filter((item) => item.score < 100 && item.top_issue);
  const healthy = items.filter((item) => !(item.score < 100 && item.top_issue));

  const header = (
    <SectionHeader
      title={
        <span className="flex items-center gap-2">
          <ListChecks className="h-5 w-5" />
          Action center
        </span>
      }
      subtitle="What to fix next, most urgent first."
    />
  );

  if (isLoading) {
    return (
      <div className="space-y-3.5">
        {header}
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3.5">
      {header}

      <div className="grid gap-3 md:grid-cols-3">
        <KpiTile label="Domains" value={items.length.toLocaleString()} hint="In view" />
        <KpiTile
          label="Need attention"
          value={needsAttention.length.toLocaleString()}
          hint="Have an open issue"
        />
        <KpiTile
          label="Healthy"
          value={healthy.length.toLocaleString()}
          hint="Nothing outstanding"
        />
      </div>

      {needsAttention.length === 0 ? (
        <CardV2>
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <CheckCircle2 className="h-10 w-10 text-[var(--v2-good)]" />
            <p className="text-[13px] font-medium text-foreground">Everything looks healthy</p>
            <p className="text-[12.5px] text-muted-foreground">
              No domain currently has an outstanding authentication issue.
            </p>
          </div>
        </CardV2>
      ) : (
        <CardV2 title="Needs attention" bodyClassName="p-0">
          <div className="divide-y divide-border">
            {needsAttention.map((item) => (
              <ActionRow key={item.domain_id} item={item} />
            ))}
          </div>
        </CardV2>
      )}

      {healthy.length > 0 && (
        <CardV2 title="Healthy domains" bodyClassName="p-0">
          <div className="divide-y divide-border">
            {healthy.map((item) => (
              <ActionRow key={item.domain_id} item={item} />
            ))}
          </div>
        </CardV2>
      )}
    </div>
  );
}
